import { useEffect } from 'react';
import { usePageSEO } from '@/hooks/usePageSEO';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import HeroArticle from './components/HeroArticle';
import TableMatieres from './components/TableMatieres';
import SectionIntro from './components/SectionIntro';
import PrixResumes from './components/PrixResumes';
import PrixParMateriau from './components/PrixParMateriau';
import FacteursPrix from './components/FacteursPrix';
import ComparatifDureeVie from './components/ComparatifDureeVie';
import Subventions from './components/Subventions';
import Economiser from './components/Economiser';
import MiseEnGarde from './components/MiseEnGarde';
import FAQPrix from './components/FAQPrix';
import CTAFinal from './components/CTAFinal';
import ArticlesLies from './components/ArticlesLies';

function setMeta(name: string, content: string) {
  let meta = document.querySelector(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', name);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

function removeOldLdJson() {
  const existing = document.querySelectorAll('script[type="application/ld+json"]');
  existing.forEach((el) => {
    if (el.id === 'blogprix-ld') el.remove();
  });
}

export default function BlogPrixPage() {
  usePageSEO({
    title: "Prix d'une toiture à Lévis en 2026 | Guide complet",
    description: "Combien coûte une nouvelle toiture à Lévis en 2026 ? Prix détaillés par matériau, facteurs influents, conseils pour économiser. Guide 2026.",
    urlPath: "/blog/prix-toiture-levis-2026",
    keywords: "prix toiture Lévis, coût toiture Lévis 2026, bardeau prix, toiture tôle prix, soumission toiture",
    ogImage: '/og/og-blog-prix-toiture-levis-2026.jpg',
    ogImageAlt: "Prix d'une toiture à Lévis en 2026 - Guide complet par matériau",
    jsonLdSchema: {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://couvreur-levis.com/' },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://couvreur-levis.com/blog' },
        { '@type': 'ListItem', position: 3, name: 'Prix toiture 2026', item: 'https://couvreur-levis.com/blog/prix-toiture-levis-2026' },
      ],
    },
  });

  useEffect(() => {
    document.title = "Prix d'une toiture à Lévis en 2026 | Guide complet";
    setMeta('description', "Combien coûte une nouvelle toiture à Lévis en 2026 ? Prix détaillés par matériau, facteurs influents, conseils pour économiser. Guide 2026.");
    setMeta('keywords', 'prix toiture Lévis, coût toiture Lévis 2026, bardeau prix, toiture tôle prix, soumission toiture');

    removeOldLdJson();

    const articleLd = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Article",
          "@id": "https://couvreur-levis.com/blog/prix-toiture-levis-2026/#article",
          headline: "Prix d'une toiture à Lévis en 2026 : Guide complet",
          description: "Combien coûte une nouvelle toiture à Lévis en 2026 ? Prix par matériau, facteurs influents, conseils.",
          image: "https://readdy.ai/api/search-image?query=Modern%20calculator%20device%20placed%20next%20to%20miniature%20model%20house%20with%20Canadian%20dollar%20bills%20scattered%20artistically%20around%2C%20clean%20professional%20flat%20lay%20photography%20on%20white%20marble%20surface%2C%20architectural%20drawings%20partially%20visible%20underneath%2C%20warm%20natural%20lighting%20from%20window%2C%20burnt%20orange%20accent%20objects%20like%20pen%20and%20ruler%2C%20deep%20charcoal%20calculator%20body%2C%20photorealistic%20high%20quality%20product%20styling%20with%20organized%20financial%20planning%20aesthetic&width=1200&height=630&seq=og-blogprix&orientation=landscape",
          author: {
            "@type": "Organization",
            name: "Couvreur-Lévis.com",
            url: "https://couvreur-levis.com",
          },
          publisher: {
            "@type": "Organization",
            name: "Couvreur-Lévis.com",
            logo: {
              "@type": "ImageObject",
              url: "https://couvreur-levis.com/og-default.jpg",
            },
          },
          datePublished: "2026-01-15",
          dateModified: "2026-05-16",
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": "https://couvreur-levis.com/blog/prix-toiture-levis-2026",
          },
        },
        {
          "@type": "FAQPage",
          "@id": "https://couvreur-levis.com/blog/prix-toiture-levis-2026/#faq",
          mainEntity: [
            {
              "@type": "Question",
              name: "Pourquoi les soumissions varient autant pour le même projet ?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Parce que chaque couvreur calcule différemment. Certains incluent l'échafaudage, d'autres pas. Certains utilisent du bardeau architectural de marque BP, d'autres du bardeau bas de gamme sans nom.",
              },
            },
            {
              "@type": "Question",
              name: "Le prix annoncé dans la soumission inclut-il vraiment tout ?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Ça dépend du couvreur. Une soumission sérieuse inclut : retrait ancienne toiture, inspection pontage, matériaux neufs, main-d'œuvre, échafaudage, solins, bardeaux de départ, vents de toit, nettoyage chantier et garantie.",
              },
            },
            {
              "@type": "Question",
              name: "Peut-on négocier le prix d'une toiture ?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "On peut discuter, oui. Mais attention : un couvreur de métier a des coûts fixes. S'il baisse de 20%, il rogne quelque part. Mieux vaut négocier des ajouts que forcer une baisse de prix.",
              },
            },
            {
              "@type": "Question",
              name: "Quel est le calendrier de paiement typique ?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Standard au Québec : acompte de 10-25% à la signature. Paiement intermédiaire de 50% au début des travaux. Solde de 25-40% à la fin, après inspection et remise de la garantie écrite.",
              },
            },
            {
              "@type": "Question",
              name: "Mon assurance habitation couvre-t-elle la réfection de toiture ?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Seulement si la réfection est causée par un sinistre couvert. L'usure normale et la fin de vie du bardeau ne sont pas couvertes.",
              },
            },
            {
              "@type": "Question",
              name: "Combien budgéter en imprévus ?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "On recommande de prévoir 10-15% de marge imprévus. Les surprises les plus fréquentes : pontage pourri caché, cheminée à refaire, puits de lumière à remplacer.",
              },
            },
          ],
        },
      ],
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'blogprix-ld';
    script.textContent = JSON.stringify(articleLd);
    document.head.appendChild(script);

    window.scrollTo(0, 0);

    return () => {
      removeOldLdJson();
    };
  }, []);

  return (
    <div className="font-inter min-h-screen bg-white">
      <Header />
      <main>
        <HeroArticle />

        <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-10 pb-16 md:pb-24">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
            <TableMatieres />

            <div className="flex-1 min-w-0 space-y-14 md:space-y-20">
              <SectionIntro />
              <PrixResumes />
              <PrixParMateriau />
              <FacteursPrix />
              <ComparatifDureeVie />
              <Subventions />
              <Economiser />
              <MiseEnGarde />
              <FAQPrix />
            </div>
          </div>
        </div>

        <CTAFinal />
        <ArticlesLies />
      </main>
      <Footer />
    </div>
  );
}